document.documentElement.classList.remove('no-js');

class NVRSAMETheme {
  constructor() {
    this.bindMobileMenu();
    this.bindSearch();
    this.bindStyleSelector();
    this.bindMotionGalleries();
    this.bindProductLightbox();
    this.bindNewsletterPopup();
  }

  bindMobileMenu() {
    const toggle = document.querySelector('[data-menu-toggle]');
    const menu = document.querySelector('[data-mobile-menu]');
    if (!toggle || !menu) return;

    toggle.addEventListener('click', () => {
      const expanded = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!expanded));
      menu.hidden = expanded;
    });

    menu.querySelectorAll('a').forEach((link) => {
      link.addEventListener('click', () => {
        menu.hidden = true;
        toggle.setAttribute('aria-expanded', 'false');
      });
    });
  }

  bindSearch() {
    const dialog = document.querySelector('[data-search-dialog]');
    if (!dialog) return;

    document.querySelectorAll('[data-search-open]').forEach((button) => {
      button.addEventListener('click', () => {
        if (typeof dialog.showModal === 'function') dialog.showModal();
        dialog.querySelector('input')?.focus();
      });
    });

    dialog.querySelector('[data-search-close]')?.addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
  }

  bindStyleSelector() {
    document.querySelectorAll('[data-motion-hero]').forEach((hero) => {
      const tabs = [...hero.querySelectorAll('[data-style-target]')];
      const panels = [...hero.querySelectorAll('[data-style-panel]')];
      if (!tabs.length || !panels.length) return;

      const order = tabs.map((tab) => tab.dataset.styleTarget);
      let index = 0;
      let timer = null;

      const activate = (target, interacted = true) => {
        hero.dataset.active = target;
        if (interacted) hero.classList.add('is-interacted');

        const position = order.indexOf(target);
        if (position > -1) index = position;

        tabs.forEach((tab) => {
          const active = tab.dataset.styleTarget === target;
          tab.classList.toggle('is-active', active);
          tab.setAttribute('aria-selected', String(active));
        });

        panels.forEach((panel) => {
          panel.classList.toggle('is-selected', panel.dataset.stylePanel === target);
          panel.hidden = false;
        });
      };

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      const stopAuto = () => {
        if (!timer) return;
        window.clearInterval(timer);
        timer = null;
        hero.classList.remove('is-autoplaying');
      };

      const startAuto = () => {
        if (timer || reduceMotion || order.length < 2) return;
        hero.classList.add('is-autoplaying');
        timer = window.setInterval(() => {
          if (document.hidden) return;
          activate(order[(index + 1) % order.length], true);
        }, 4500);
      };

      tabs.forEach((tab) => {
        tab.addEventListener('click', () => {
          stopAuto();
          activate(tab.dataset.styleTarget);
        });
      });

      hero.addEventListener('pointerdown', stopAuto, { once: true });

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) return;
        if (hero.classList.contains('is-autoplaying')) activate(order[index], true);
      });

      activate(order[0], false);
      startAuto();
    });
  }

  bindMotionGalleries() {
    document.querySelectorAll('[data-motion-gallery]').forEach((gallery) => {
      const track = gallery.querySelector('[data-motion-track]');
      const previous = gallery.querySelector('[data-motion-prev]');
      const next = gallery.querySelector('[data-motion-next]');
      if (!track) return;

      const move = (direction) => {
        const distance = Math.max(track.clientWidth * 0.72, 280);
        track.scrollBy({ left: direction * distance, behavior: 'smooth' });
      };

      previous?.addEventListener('click', () => move(-1));
      next?.addEventListener('click', () => move(1));
    });
  }

  bindProductLightbox() {
    const dialog = document.querySelector('[data-product-lightbox]');
    const gallery = document.querySelector('[data-product-gallery]');
    if (!dialog || !gallery || typeof dialog.showModal !== 'function') return;

    const triggers = [...gallery.querySelectorAll('[data-zoom-src]')];
    if (!triggers.length) return;

    const image = dialog.querySelector('[data-lightbox-image]');
    const counter = dialog.querySelector('[data-lightbox-counter]');
    const previous = dialog.querySelector('[data-lightbox-prev]');
    const next = dialog.querySelector('[data-lightbox-next]');
    const single = triggers.length < 2;
    let index = 0;

    if (single) {
      previous?.setAttribute('hidden', '');
      next?.setAttribute('hidden', '');
    }

    const show = (position) => {
      index = (position + triggers.length) % triggers.length;
      const trigger = triggers[index];
      image.src = trigger.dataset.zoomSrc;
      image.alt = trigger.dataset.zoomAlt || '';
      if (counter) counter.textContent = single ? '' : `${index + 1} / ${triggers.length}`;
    };

    triggers.forEach((trigger, position) => {
      trigger.addEventListener('click', () => {
        show(position);
        dialog.showModal();
      });
    });

    previous?.addEventListener('click', () => show(index - 1));
    next?.addEventListener('click', () => show(index + 1));
    dialog.querySelector('[data-lightbox-close]')?.addEventListener('click', () => dialog.close());

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog || event.target.classList.contains('product-lightbox__stage')) dialog.close();
    });

    dialog.addEventListener('keydown', (event) => {
      if (single) return;
      if (event.key === 'ArrowLeft') { event.preventDefault(); show(index - 1); }
      if (event.key === 'ArrowRight') { event.preventDefault(); show(index + 1); }
    });

    dialog.addEventListener('close', () => {
      triggers[index]?.focus();
    });
  }

  bindNewsletterPopup() {
    const popup = document.querySelector('[data-newsletter-popup]');
    if (!popup) return;

    const KEY = 'nvrsame:newsletter-popup';
    const days = parseInt(popup.dataset.npDays || '14', 10);
    const delay = Math.max(parseFloat(popup.dataset.npDelay || '3'), 0) * 1000;
    const forced = window.NVRSAME_POPUP_FORCE === true;

    const read = () => {
      try { return window.localStorage.getItem(KEY); } catch (e) { return null; }
    };
    const write = () => {
      try { window.localStorage.setItem(KEY, String(Date.now())); } catch (e) { /* ignore */ }
    };

    if (!forced && days > 0) {
      const last = parseInt(read() || '0', 10);
      if (last && Date.now() - last < days * 86400000) return;
    }

    let opened = false;

    const open = () => {
      if (opened) return;
      opened = true;
      popup.hidden = false;
      requestAnimationFrame(() => popup.classList.add('is-open'));
      document.body.classList.add('np-open');
      popup.querySelector('input[type="email"]')?.focus({ preventScroll: true });
    };

    const close = () => {
      popup.classList.remove('is-open');
      document.body.classList.remove('np-open');
      write();
      window.setTimeout(() => { popup.hidden = true; }, 320);
    };

    popup.querySelectorAll('[data-np-close]').forEach((el) => el.addEventListener('click', close));
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && popup.classList.contains('is-open')) close();
    });
    popup.querySelector('form')?.addEventListener('submit', write);

    if (forced) open();
    else window.setTimeout(open, delay);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new NVRSAMETheme());
} else {
  new NVRSAMETheme();
}
